import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
    FileText,
    Loader2,
    Plus,
    Trash2,
    Save,
    Upload,
    ArrowLeft,
    Eye,
    GripVertical,
    EyeOff,
    Mail,
} from "lucide-react";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RichTextEditor from "@/components/RichTextEditor";
import { articles as staticArticles } from "@/data/articles";
import { articleToHtml } from "@/lib/articleToHtml";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (err) => {
    // err can be an axios error (with .response.data.detail) or a plain string.
    // Surface as much detail as possible so users don't see a mysterious
    // "Something went wrong" — status codes and validation messages are
    // far more actionable.
    if (!err) return "Something went wrong. Please try again.";
    if (typeof err === "string") return err;

    // Direct detail (already-extracted) case
    const d = err.detail !== undefined ? err.detail : err;

    if (typeof d === "string" && d.length > 0) return d;
    if (Array.isArray(d)) {
        const msg = d.map((e) => e?.msg || "").filter(Boolean).join(" · ");
        if (msg) return msg;
    }
    if (d && typeof d === "object" && d.msg) return d.msg;

    // Axios-error shape
    const resp = err.response;
    if (resp) {
        const detail = resp.data?.detail;
        if (typeof detail === "string" && detail) return `${resp.status}: ${detail}`;
        if (Array.isArray(detail)) {
            const msg = detail.map((e) => e?.msg || "").filter(Boolean).join(" · ");
            if (msg) return `${resp.status}: ${msg}`;
        }
        return `Server responded ${resp.status} ${resp.statusText || ""}`.trim();
    }
    if (err.message?.includes("Network Error"))
        return "Network error — check your connection and try again.";
    if (err.message) return err.message;
    return "Something went wrong. Please try again.";
};

const slugify = (s) =>
    (s || "")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/&/g, "-and-")
        .replace(/[^a-z0-9\s_-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64);

function StatusBadge({ status }) {
    const isPublished = status === "published";
    return (
        <span
            data-testid={`article-status-${status || "draft"}`}
            className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] px-2 py-0.5 border ${
                isPublished
                    ? "border-[color:var(--charcoal)] bg-[color:var(--charcoal)] text-white"
                    : "border-[color:var(--charcoal)]/40 text-[color:var(--slate)]"
            }`}
        >
            {isPublished ? <Eye size={10} strokeWidth={1.8} /> : <EyeOff size={10} strokeWidth={1.8} />}
            {isPublished ? "Published" : "Draft"}
        </span>
    );
}

function ArticleForm({ token, initial, onSaved, onCancel }) {
    const [existingSlug, setExistingSlug] = useState(initial?.slug || null);
    const isNew = !existingSlug;
    const [meta, setMeta] = useState({
        slug: initial?.slug || "",
        title: initial?.title || "",
        subtitle: initial?.subtitle || "",
        kicker: initial?.kicker || "",
        number: initial?.number || "",
        read_minutes: initial?.read_minutes ?? 5,
        status: initial?.status || "draft",
    });
    const [bodyHtml, setBodyHtml] = useState(initial?.body_html || "");
    const [pdfInfo, setPdfInfo] = useState({
        pdf_url: initial?.pdf_url || "",
        pdf_filename: initial?.pdf_filename || "",
    });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);

    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const onTitleChange = (v) => {
        setMeta((m) => ({
            ...m,
            title: v,
            slug: isNew && (!m.slug || m.slug === slugify(m.title)) ? slugify(v) : m.slug,
        }));
    };

    const save = async (opts) => {
        opts?.preventDefault?.();
        const publishToggle = opts && opts.overrideStatus;
        setError(null);
        setStatus(null);
        setBusy(true);
        try {
            const slug = meta.slug || slugify(meta.title);
            if (!slug) throw new Error("Slug required");
            const nextStatus = publishToggle || meta.status || "draft";
            const payload = {
                ...meta,
                slug,
                body_html: bodyHtml,
                read_minutes: Number(meta.read_minutes) || 0,
                status: nextStatus,
            };
            if (isNew) {
                await axios.post(`${API}/admin/articles`, { ...payload, thread_id: slug, ...pdfInfo }, { headers });
                setExistingSlug(slug);
            } else {
                await axios.put(`${API}/admin/articles/${existingSlug}`, payload, { headers });
            }
            setMeta((m) => ({ ...m, status: nextStatus }));
            setStatus(nextStatus === "published" ? "Published." : "Saved as draft.");
            onSaved?.(slug);
        } catch (err) {
            setError(formatError(err));
        } finally {
            setBusy(false);
        }
    };

    const uploadPdf = async () => {
        if (!file) {
            setError("Please choose a PDF first.");
            return;
        }
        const slug = meta.slug || slugify(meta.title);
        if (!slug || !meta.title) {
            setError("Please set a title first.");
            return;
        }
        setError(null);
        setStatus(null);
        setBusy(true);
        try {
            if (isNew) {
                await axios.post(
                    `${API}/admin/articles`,
                    {
                        ...meta,
                        slug,
                        thread_id: slug,
                        body_html: bodyHtml,
                        read_minutes: Number(meta.read_minutes) || 0,
                        status: meta.status || "draft",
                    },
                    { headers },
                );
                setExistingSlug(slug);
            }
            const form = new FormData();
            form.append("file", file);
            const res = await axios.post(
                `${API}/admin/articles/${slug}/upload-pdf`,
                form,
                { headers: { ...headers, "Content-Type": "multipart/form-data" } },
            );
            const { draft_html, pdf_url, words } = res.data;
            setPdfInfo({ pdf_url, pdf_filename: `${meta.title.replace(/\s+/g, "_")}.pdf` });
            if (draft_html) {
                setBodyHtml(draft_html);
                setStatus(`PDF uploaded. Extracted ${words || 0} words into the editor — clean it up and save.`);
            } else {
                setStatus(`PDF uploaded (${words || 0} words extracted). This looks like a scanned/image PDF — you'll need to type the content manually.`);
            }
            onSaved?.(slug, { silent: true });
        } catch (err) {
            setError(formatError(err));
        } finally {
            setBusy(false);
        }
    };

    // Pull the currently-rendered article body (from the structured
    // `articles.js` data used to render the reader page) directly into the
    // editor. Handy for legacy static articles that never had a `body_html`
    // — this is the reverse of "Extract & fill from PDF" and unlocks the
    // Regenerate-PDF flow without needing a source PDF file.
    const pullFromSite = () => {
        const slug = existingSlug || meta.slug;
        if (!slug) {
            setError("Save the article first, then pull.");
            return;
        }
        const src = staticArticles.find((a) => a.slug === slug);
        if (!src) {
            setError(
                "No matching entry in the site's structured content. This article isn't rendered from `articles.js`, so there's nothing to pull.",
            );
            return;
        }
        const html = articleToHtml(src);
        if (!html) {
            setError("Structured content is empty — nothing to pull.");
            return;
        }
        if (
            (bodyHtml || "").trim()
            && !window.confirm(
                "The editor already has content. Replace it with the version currently rendered on the site?",
            )
        )
            return;
        setBodyHtml(html);
        setError(null);
        setStatus(
            "Pulled the currently-rendered article into the editor. Review, save, then Regenerate PDF.",
        );
    };

    // Regenerate the downloadable PDF from the current editor content. The
    // "Download PDF" button on the reader page serves whatever file was last
    // uploaded — once you start editing the body, that file drifts from what
    // readers see on the site. Hitting this button re-renders a fresh PDF
    // (title page + editor HTML) via weasyprint on the backend and
    // overwrites the file at `/api/files/{slug}.pdf`.
    const regeneratePdf = async () => {
        const slug = existingSlug || meta.slug || slugify(meta.title);
        if (!slug) {
            setError("Save the article first, then regenerate the PDF.");
            return;
        }
        const inner = (bodyHtml || "").trim();
        if (!inner) {
            setError(
                "Nothing to render yet — paste or type content in the editor first.",
            );
            return;
        }
        if (
            !window.confirm(
                "This will overwrite the current downloadable PDF for this article. Continue?",
            )
        )
            return;
        setError(null);
        setStatus(null);
        setBusy(true);
        try {
            const res = await axios.post(
                `${API}/admin/articles/${slug}/regenerate-pdf`,
                { html: inner },
                { headers },
            );
            const { url, filename, bytes } = res.data;
            setPdfInfo({ pdf_url: url, pdf_filename: filename });
            const kb = Math.max(1, Math.round((bytes || 0) / 1024));
            setStatus(`New PDF rendered (${kb} KB). Readers now download the fresh version.`);
        } catch (err) {
            setError(formatError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={save} data-testid="article-form" className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <StatusBadge status={meta.status} />
                {!isNew && (
                    <span className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                        Editing /{existingSlug}
                    </span>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block">
                    <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                        Title *
                    </span>
                    <input
                        type="text"
                        required
                        value={meta.title}
                        onChange={(e) => onTitleChange(e.target.value)}
                        data-testid="article-input-title"
                        className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                    />
                </label>
                <label className="block">
                    <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                        Slug (URL, immutable once created)
                    </span>
                    <input
                        type="text"
                        required
                        disabled={!isNew}
                        value={meta.slug}
                        onChange={(e) => setMeta({ ...meta, slug: slugify(e.target.value) })}
                        data-testid="article-input-slug"
                        className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-mono text-sm disabled:opacity-60"
                    />
                </label>
                <label className="block md:col-span-2">
                    <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                        Subtitle
                    </span>
                    <input
                        type="text"
                        value={meta.subtitle}
                        onChange={(e) => setMeta({ ...meta, subtitle: e.target.value })}
                        data-testid="article-input-subtitle"
                        className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                    />
                </label>
                <label className="block">
                    <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                        Kicker (e.g. “The Manifesto”)
                    </span>
                    <input
                        type="text"
                        value={meta.kicker}
                        onChange={(e) => setMeta({ ...meta, kicker: e.target.value })}
                        data-testid="article-input-kicker"
                        className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                    />
                </label>
                <div className="grid grid-cols-2 gap-4">
                    <label className="block">
                        <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                            Number (I, II…)
                        </span>
                        <input
                            type="text"
                            value={meta.number}
                            onChange={(e) => setMeta({ ...meta, number: e.target.value })}
                            data-testid="article-input-number"
                            className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                        />
                    </label>
                    <label className="block">
                        <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-1.5">
                            Min read
                        </span>
                        <input
                            type="number"
                            min={1}
                            max={120}
                            value={meta.read_minutes}
                            onChange={(e) => setMeta({ ...meta, read_minutes: e.target.value })}
                            data-testid="article-input-read-minutes"
                            className="w-full bg-transparent border-b border-[color:var(--charcoal)]/40 focus:border-[color:var(--charcoal)] outline-none py-2 font-body text-base"
                        />
                    </label>
                </div>
            </div>

            <div className="border border-[color:var(--charcoal)]/30 p-5 bg-[color:var(--cloud)]">
                <div className="flex items-center gap-2 mb-3">
                    <Upload size={13} strokeWidth={1.7} />
                    <span className="font-mono text-[12px] uppercase tracking-[0.24em]">
                        Import from PDF (optional)
                    </span>
                </div>
                <p className="font-body text-sm text-[color:var(--charcoal)]/85 mb-4 max-w-[62ch]">
                    Upload a PDF and the server will run text extraction (and OCR
                    for scans if available) then drop the result into the editor
                    below for you to clean up. The original PDF becomes the
                    downloadable file for readers.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        data-testid="article-pdf-file"
                        className="font-body text-sm"
                    />
                    <button
                        type="button"
                        onClick={uploadPdf}
                        disabled={busy || !file}
                        data-testid="article-pdf-upload"
                        className="pill-btn"
                        style={{ padding: "0.55rem 1rem" }}
                    >
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={1.7} />}
                        Extract & fill editor
                    </button>
                    {pdfInfo.pdf_url && (
                        <a
                            href={pdfInfo.pdf_url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="article-pdf-preview"
                            className="pill-btn ghost"
                            style={{ padding: "0.5rem 0.9rem" }}
                        >
                            <Eye size={12} strokeWidth={1.7} />
                            Current PDF
                        </a>
                    )}
                    <button
                        type="button"
                        onClick={pullFromSite}
                        disabled={busy}
                        data-testid="article-pull-from-site"
                        className="pill-btn ghost"
                        style={{ padding: "0.5rem 0.9rem" }}
                        title="Copy the currently-rendered article text on the site into the editor"
                    >
                        <ArrowLeft size={13} strokeWidth={1.7} />
                        Pull from site
                    </button>
                    <button
                        type="button"
                        onClick={regeneratePdf}
                        disabled={busy || !(bodyHtml || "").trim()}
                        data-testid="article-pdf-regenerate"
                        className="pill-btn ghost"
                        style={{ padding: "0.5rem 0.9rem" }}
                        title={
                            (bodyHtml || "").trim()
                                ? "Render a fresh downloadable PDF from the current editor content"
                                : "Add content to the editor first"
                        }
                    >
                        {busy ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <FileText size={13} strokeWidth={1.7} />
                        )}
                        Regenerate PDF
                    </button>
                </div>
                <p className="mt-3 font-body text-[13px] leading-[1.55] text-[color:var(--charcoal)]/60 max-w-[60ch]">
                    <strong>Extract & fill editor</strong> ingests an uploaded
                    PDF and drops the extracted text into the editor for you
                    to clean up. <strong>Pull from site</strong> yanks the
                    currently-rendered article text straight into the editor
                    (handy when there&apos;s no source PDF).{" "}
                    <strong>Regenerate PDF</strong> renders a fresh
                    downloadable PDF from the current editor content so the
                    file readers download always matches the site.
                </p>
            </div>

            <div>
                <span className="block font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mb-2">
                    Article body (WYSIWYG · drag images from your desktop or use the image button)
                </span>
                <RichTextEditor value={bodyHtml} onChange={setBodyHtml} token={token} />
            </div>

            {status && (
                <div data-testid="article-status" className="p-3 border border-[color:var(--charcoal)]/40 bg-[color:var(--cloud)] font-body text-sm">
                    {status}
                </div>
            )}
            {error && (
                <div data-testid="article-error" className="p-3 border border-[color:var(--charcoal)] font-body text-sm">
                    {error}
                </div>
            )}
            <div className="flex flex-wrap gap-3 justify-end items-center">
                <button
                    type="button"
                    onClick={onCancel}
                    data-testid="article-form-cancel"
                    className="pill-btn ghost"
                    style={{ padding: "0.6rem 1rem" }}
                >
                    <ArrowLeft size={13} strokeWidth={1.7} />
                    Back to list
                </button>
                <button
                    type="button"
                    onClick={() => save({ overrideStatus: "draft" })}
                    disabled={busy}
                    data-testid="article-form-save-draft"
                    className="pill-btn ghost"
                    style={{ padding: "0.6rem 1rem" }}
                >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} strokeWidth={1.7} />}
                    Save draft
                </button>
                <button
                    type="button"
                    onClick={() => save({ overrideStatus: "published" })}
                    disabled={busy}
                    data-testid="article-form-save"
                    className="pill-btn"
                    style={{ padding: "0.6rem 1.2rem" }}
                >
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} strokeWidth={1.7} />}
                    {meta.status === "published" ? "Update & keep published" : "Publish"}
                </button>
            </div>
        </form>
    );
}

function SortableRow({ article, onEdit, onDelete, onQuickPublish, onCompose }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: article.slug,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        background: isDragging ? "rgba(13,19,23,0.03)" : undefined,
    };

    return (
        <li
            ref={setNodeRef}
            style={style}
            data-testid={`article-row-${article.slug}`}
            className="py-5 grid grid-cols-12 gap-4 items-center border-b border-[color:var(--charcoal)]/15 last:border-b-0"
        >
            <div className="col-span-12 md:col-span-1 flex items-center">
                <button
                    type="button"
                    aria-label={`Drag to reorder ${article.title}`}
                    data-testid={`article-drag-${article.slug}`}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-[color:var(--charcoal)]/8 cursor-grab active:cursor-grabbing touch-none"
                    {...attributes}
                    {...listeners}
                >
                    <GripVertical size={16} strokeWidth={1.6} />
                </button>
            </div>
            <div className="col-span-12 md:col-span-6">
                <div className="font-display italic text-lg md:text-xl flex items-center gap-3 flex-wrap">
                    {article.title}
                    <StatusBadge status={article.status} />
                </div>
                <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)] mt-1">
                    /{article.slug} · {article.read_minutes || 0} min read
                    {article.pdf_url && " · PDF attached"}
                </div>
            </div>
            <div className="col-span-12 md:col-span-5 flex md:justify-end gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={() => onQuickPublish(article)}
                    data-testid={`article-toggle-status-${article.slug}`}
                    className="pill-btn ghost"
                    style={{ padding: "0.5rem 0.9rem" }}
                >
                    {article.status === "published" ? (
                        <>
                            <EyeOff size={12} strokeWidth={1.7} />
                            Unpublish
                        </>
                    ) : (
                        <>
                            <Eye size={12} strokeWidth={1.7} />
                            Publish
                        </>
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => onEdit(article.slug)}
                    data-testid={`article-edit-${article.slug}`}
                    className="pill-btn ghost"
                    style={{ padding: "0.5rem 0.9rem" }}
                >
                    Edit
                </button>
                <button
                    type="button"
                    onClick={() => onCompose(article)}
                    data-testid={`article-compose-newsletter-${article.slug}`}
                    className="pill-btn ghost"
                    style={{ padding: "0.5rem 0.9rem" }}
                    title="Draft a Stay Informed dispatch about this article"
                >
                    <Mail size={12} strokeWidth={1.7} />
                    Compose dispatch
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(article.slug)}
                    data-testid={`article-delete-${article.slug}`}
                    className="pill-btn ghost"
                    style={{ padding: "0.5rem 0.9rem" }}
                >
                    <Trash2 size={12} strokeWidth={1.7} />
                    Delete
                </button>
            </div>
        </li>
    );
}

export default function ArticlesTab({ token, initialEditSlug = null, onComposeNewsletter }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(null);
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${API}/admin/articles`, { headers });
            setItems(res.data || []);
        } catch (e) {
            setError(formatError(e));
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Deep-link: once the article list has loaded, open the requested slug — but only once.
    // A ref prevents the effect from re-firing after we clear `editing` on save.
    const openedInitialRef = useRef(false);
    useEffect(() => {
        if (
            initialEditSlug
            && !openedInitialRef.current
            && items.length > 0
        ) {
            openedInitialRef.current = true;
            setEditing(initialEditSlug);
            // Clean the URL so refreshing doesn't force the editor open again.
            if (typeof window !== "undefined" && window.history?.replaceState) {
                const url = new URL(window.location.href);
                url.searchParams.delete("edit");
                window.history.replaceState({}, "", url.toString());
            }
        }
    }, [initialEditSlug, items]);

    const del = async (slug) => {
        if (!window.confirm(`Delete article “${slug}”? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/admin/articles/${slug}`, { headers });
            refresh();
        } catch (e) {
            setError(formatError(e));
        }
    };

    const toggleStatus = async (article) => {
        const next = article.status === "published" ? "draft" : "published";
        try {
            await axios.put(`${API}/admin/articles/${article.slug}`, { status: next }, { headers });
            refresh();
        } catch (e) {
            setError(formatError(e));
        }
    };

    // Build a Stay Informed prefill from an article and hand it up to Admin,
    // which will switch to the Home Copy tab where NewsletterPanel picks it up.
    const composeDispatch = (article) => {
        const subtitle = (article.subtitle || "").trim();
        const title = article.title || "";
        const kicker = (article.kicker || "").trim();
        const site = "https://equitynation.ca";
        const link = `${site}/#article-${article.slug}`;
        const prefill = {
            subject: `New chapter is live — ${title}`,
            intro:
                subtitle
                    ? `A quick note from Equity Nation: “${title}” — ${subtitle} — is now live.`
                    : `A quick note from Equity Nation: “${title}” is now live.`,
            body:
                (kicker ? `${kicker.toUpperCase()} · ` : "")
                + `Read the new piece and share your thoughts in the thread. `
                + `We keep it plain-language, source-cited, and open to disagreement.`,
            cta_label: "Read it now",
            cta_url: link,
        };
        try {
            sessionStorage.setItem("newsletter_prefill", JSON.stringify(prefill));
        } catch {
            // sessionStorage might be blocked in some browsers — non-fatal.
        }
        onComposeNewsletter?.(prefill);
    };

    const onDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = items.findIndex((a) => a.slug === active.id);
        const newIdx = items.findIndex((a) => a.slug === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const next = arrayMove(items, oldIdx, newIdx);
        setItems(next); // optimistic
        try {
            await axios.post(
                `${API}/admin/articles/reorder`,
                { slugs: next.map((a) => a.slug) },
                { headers },
            );
        } catch (e) {
            setError(formatError(e));
            refresh(); // rollback from server
        }
    };

    if (editing) {
        const initial =
            editing === "__new__" ? null : items.find((a) => a.slug === editing);
        return (
            <div data-testid="articles-editor">
                <ArticleForm
                    token={token}
                    initial={initial}
                    onSaved={(slug, opts) => {
                        refresh();
                        if (!opts?.silent) setEditing(null);
                    }}
                    onCancel={() => setEditing(null)}
                />
            </div>
        );
    }

    return (
        <div data-testid="articles-tab">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <span className="font-mono text-[12px] uppercase tracking-[0.24em] text-[color:var(--slate)]">
                    {items.length} article{items.length === 1 ? "" : "s"} in CMS · drag to reorder on the home page
                </span>
                <button
                    type="button"
                    onClick={() => setEditing("__new__")}
                    data-testid="articles-new"
                    className="pill-btn"
                    style={{ padding: "0.55rem 1rem" }}
                >
                    <Plus size={13} strokeWidth={1.7} />
                    New article
                </button>
            </div>

            {error && (
                <div className="mb-4 p-3 border border-[color:var(--charcoal)] font-body text-sm">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                    <Loader2 size={14} className="animate-spin" />
                    Loading
                </div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-[color:var(--charcoal)]/30 p-8 text-center font-body text-[color:var(--charcoal)]/85">
                    <FileText size={22} strokeWidth={1.4} className="mx-auto mb-3 opacity-60" />
                    <p className="mb-2">No CMS articles yet.</p>
                    <p className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--slate)]">
                        Legacy hard-coded articles (Equity Nation, Our Economy, Concept) still show on the site.
                        Upload a PDF here to create new articles or replace the existing ones by using the same slug.
                    </p>
                </div>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                    <SortableContext items={items.map((a) => a.slug)} strategy={verticalListSortingStrategy}>
                        <ul data-testid="articles-list">
                            {items.map((a) => (
                                <SortableRow
                                    key={a.slug}
                                    article={a}
                                    onEdit={setEditing}
                                    onDelete={del}
                                    onQuickPublish={toggleStatus}
                                    onCompose={composeDispatch}
                                />
                            ))}
                        </ul>
                    </SortableContext>
                </DndContext>
            )}
        </div>
    );
}
